import { formatMoney } from './js-currency.js';

const FreeShippingBar = class extends HTMLElement {
	static get observedAttributes() {
		return ['threshold', 'total-price'];
	}
	constructor() {
		super();
		this._onCartChangedListener = this._onCartChanged.bind(this);
	}
	async connectedCallback() {
		this.threshold = Math.round(this.threshold * (Shopify.currency.rate || 1));
		document.addEventListener('cart:change', this._onCartChangedListener);
		// Call _updateMessage on initial connection
		this._updateMessage();
	}
	disconnectedCallback() {
		document.removeEventListener('cart:change', this._onCartChangedListener);
	}
	get threshold() {
		return Number.parseFloat(this.getAttribute('threshold'));
	}
	set threshold(value) {
		this.setAttribute('threshold', value);
	}
	get totalPrice() {
		return Number.parseFloat(this.getAttribute('total-price'));
	}
	set totalPrice(value) {
		this.setAttribute('total-price', value);
	}
	async attributeChangedCallback(attribute, oldValue, newValue) {
		await window.customElements.whenDefined('progress-bar');
		const progressBarElement = this.querySelector('progress-bar');
		if (!progressBarElement) {
			return;
		}
		switch (attribute) {
			case 'threshold':
				progressBarElement.valueMax = newValue;
				break;
			case 'total-price':
				progressBarElement.valueNow = newValue;
				break;
		}
		this._updateMessage();
	}
	_updateMessage() {
		const messageElement = this.querySelector('span');
		if (!messageElement) {
			return;
		}
		if (this.totalPrice >= this.threshold) {
			messageElement.innerHTML = this.getAttribute('reached-message');
		} else {
			const unreachedMsg = this.getAttribute('unreached-message');
			const remaining = this.threshold - this.totalPrice;
			const moneyFormat = this.dataset.moneyFormat;
			// Use Shopify.formatMoney if available, otherwise use formatMoney with the format
			const formattedAmount =
				window?.Shopify?.formatMoney && moneyFormat
					? window.Shopify.formatMoney(remaining, moneyFormat)
					: formatMoney(
							remaining,
							moneyFormat || window.themeVariables?.settings?.moneyFormat,
						);
			const replacement = `<span class="bold text-accent">${formattedAmount}</span>`;
			// Replace {{ remaining_amount }} or {% raw %}{{ remaining_amount }}{% endraw %} patterns
			const finalMessage = unreachedMsg.replace(
				/\{\{\s*remaining_amount\s*\}\}|\{%\s*raw\s*%\}(?:\{\{.*?\}\})\{%\s*endraw\s*%\}/g,
				replacement,
			);
			messageElement.innerHTML = finalMessage;
		}
	}
	_onCartChanged(event) {
		this.totalPrice = event.detail.cart.items
			.filter((item) => item.requires_shipping)
			.reduce((sum, item) => sum + item.final_line_price, 0);
	}
};
if (!window.customElements.get('free-shipping-bar')) {
	window.customElements.define('free-shipping-bar', FreeShippingBar);
}

const LineItemQuantity = class extends HTMLElement {
	constructor() {
		super();
		this.addEventListener('change', this._onQuantityChanged);
		this.addEventListener('click', this._onRemoveLinkClicked);
	}
	_onQuantityChanged(event) {
		if (!event.target.hasAttribute('data-line-key')) {
			return;
		}
		this._changeLineItemQuantity(
			event.target.getAttribute('data-line-key'),
			parseInt(event.target.value),
		);
	}
	_onRemoveLinkClicked(event) {
		const anchor = event.target.closest?.('a');
		if (!anchor || !anchor.href.includes('/cart/change')) {
			return;
		}
		event.preventDefault();
		const url = new URL(anchor.href);
		this._changeLineItemQuantity(
			url.searchParams.get('id'),
			parseInt(url.searchParams.get('quantity')),
		);
	}
	async _changeLineItemQuantity(lineKey, targetQuantity) {
		if (window.themeVariables.settings.pageType === 'cart') {
			window.location.href = `${Shopify.routes.root}cart/change?id=${lineKey}&quantity=${targetQuantity}`;
		} else {
			const lineItem = this.closest('line-item');
			lineItem?.dispatchEvent(
				new CustomEvent('line-item:will-change', {
					bubbles: true,
					detail: { targetQuantity },
				}),
			);
			let sectionsToBundle = [];
			document.documentElement.dispatchEvent(
				new CustomEvent('cart:prepare-bundled-sections', {
					bubbles: true,
					detail: { sections: sectionsToBundle },
				}),
			);
			const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					id: lineKey,
					quantity: targetQuantity,
					sections: sectionsToBundle,
				}),
			});

			const cartContent = await response.json().catch(() => null);
			const items = Array.isArray(cartContent?.items)
				? cartContent.items
				: null;
			const lineItemAfterChange = items
				? items.filter((lineItem2) => lineItem2['key'] === lineKey)
				: [];
			lineItem?.dispatchEvent(
				new CustomEvent('line-item:change', {
					bubbles: true,
					detail: {
						quantity:
							lineItemAfterChange.length === 0
								? 0
								: lineItemAfterChange[0]['quantity'],
						cart: cartContent || {},
					},
				}),
			);
			document.documentElement.dispatchEvent(
				new CustomEvent('cart:change', {
					bubbles: true,
					detail: {
						baseEvent: 'line-item:change',
						cart: cartContent || {},
					},
				}),
			);

			// If the response is missing the expected structure (or a third-party app mutates the cart again),
			// force a refresh so the cart drawer can re-sync from the server.
			if (!items) {
				document.documentElement.dispatchEvent(
					new CustomEvent('cart:refresh', { bubbles: true }),
				);
			}
		}
	}
};
if (!window.customElements.get('line-item-quantity')) {
	window.customElements.define('line-item-quantity', LineItemQuantity);
}

const ProgressBar = class extends HTMLElement {
	static get observedAttributes() {
		return ['aria-valuenow', 'aria-valuemax'];
	}
	set valueMax(value) {
		this.setAttribute('aria-valuemax', value);
	}
	set valueNow(value) {
		this.setAttribute('aria-valuenow', value);
	}
	attributeChangedCallback() {
		this.style.setProperty(
			'--progress',
			`${Math.min(1, this.getAttribute('aria-valuenow') / this.getAttribute('aria-valuemax'))}`,
		);
	}
};
if (!window.customElements.get('progress-bar')) {
	window.customElements.define('progress-bar', ProgressBar);
}
